/**
 * @fileoverview Own the timing inputs within the shared Redux calendar draft. All-day versus timed mode and selected timezone determine how user calendar values cross into stored ISO fields. Event persistence belongs to the enclosing save action; changing the picker alone does not update a local or remote calendar event.
 */

import { useSelector, useDispatch } from 'react-redux'
import api from '../../api'
import axios from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
    setStartDateTime12h,
    setEndDateTime12h,
    setDaterange,
    setDate,
    setAllday,
    setTimeZone,
} from '../../store/CalendarInput/action'
import { Dropdown } from 'primereact/dropdown'
import { RadioButton } from 'primereact/radiobutton'
import GlassSwitch from './GlassSwitch'
import DateTimeRangePicker from '@wojtekmaj/react-datetimerange-picker'
import DatePicker from 'react-date-picker'
import '@wojtekmaj/react-datetimerange-picker/dist/DateTimeRangePicker.css'
import 'react-date-picker/dist/DatePicker.css'
import 'react-calendar/dist/Calendar.css'
import 'react-clock/dist/Clock.css'
import './datepickers-dark.css'




const DateTimeInfo = () => {
    const dispatch = useDispatch()
    const startDatetime12h = useSelector(state => state.calendarInput.startDatetime12h);
    const endDatetime12h = useSelector(state => state.calendarInput.endDatetime12h);
    const daterange = useSelector(state => state.calendarInput.daterange);
    const date = useSelector(state => state.calendarInput.date);
    const allday = useSelector(state => state.calendarInput.allday)
    const timeZone = useSelector(state => state.calendarInput.timeZone)
    const timeZones = useSelector(state => state.calendarInput.timeZones)
    const prevStartDateRef = useRef(null)

    const [animate, setAnimate] = useState(false)
    const prevAlldayRef = useRef(allday)



    useEffect(

    () => {
        // Only sync when allday actually changes (user toggled the switch)
        const alldayChanged = prevAlldayRef.current !== allday;
        
        if (alldayChanged) {
            if (allday && daterange && daterange[0]) {
                // Switched to all-day mode: sync date from daterange
                const rangeStartDate = new Date(daterange[0]);
                dispatch(setDate(rangeStartDate.toISOString()));
                console.log('Synced date from daterange (switched to all-day):', rangeStartDate);
            } else if (!allday && date) {
                // Switched to timed mode: sync daterange from date
                const dateValue = new Date(date);
                const endDate = new Date(dateValue);
                endDate.setHours(dateValue.getHours() + 1);
                dispatch(setDaterange([dateValue.toISOString(), endDate.toISOString()]));
                console.log('Synced daterange from date (switched to timed):', dateValue);
            }
            
            // Update the ref for next comparison
            prevAlldayRef.current = allday;
        }
    }, [allday, date, daterange, dispatch])

    useEffect(

    () => {
        let currentDate = new Date(startDatetime12h)
        currentDate.setHours(currentDate.getHours() + 1)
        let newEndDateTime = currentDate.toISOString()
        // Compare with the end date time from the state
        if (newEndDateTime !== endDatetime12h) {
            dispatch(setEndDateTime12h(newEndDateTime))
            setAnimate(true)
            setTimeout(() => setAnimate(false), 300)
        }
    }, [startDatetime12h, endDatetime12h, dispatch])

    useEffect(

    () => {
        if (daterange === null) {
            prevStartDateRef.current = null
            return
        }

        const [currentStartDate, currentEndDate] = [new Date(daterange[0]), new Date(daterange[1])]
        const prevStartDate = prevStartDateRef.current

        if (
            (prevStartDate === null && currentStartDate !== null) ||
            (prevStartDate !== null && currentStartDate.getTime() !== prevStartDate.getTime())
        ) {
            // Execute the code that should run when the start date changes
            updateStateBasedOnStartDate(currentStartDate)
        }

        // Update previous start date ref for the next render
        prevStartDateRef.current = currentStartDate

    }, [daterange])

    const updateStateBasedOnStartDate =

    (currentStartDate) => {
        const newEndDate = new Date(currentStartDate)
        newEndDate.setHours(currentStartDate.getHours() + 1)
        dispatch(setDaterange([currentStartDate.toISOString(), newEndDate.toISOString()]))
        dispatch(setStartDateTime12h(currentStartDate.toISOString()))
        setAnimate(true)
        setTimeout(() => setAnimate(false), 300)
    }





    return (
        <>
            {/* All Day toggle and Date/Time picker in one cell for compact layout */}
            <div className="field mb-3 col-12">
                <div className="flex align-items-center gap-3 flex-wrap">
                    <div className="flex align-items-center gap-2">
                        <label htmlFor="allday" className="font-medium text-900 mb-0">All Day</label>
                        <GlassSwitch checked={allday} onChange={(e) => dispatch(setAllday(e.value))} />
                    </div>
                    
                    <div className="flex align-items-center gap-2" style={{ position: 'relative', zIndex: 10001 }}>
                        <label htmlFor="datetime" className="font-medium text-900 mb-0">
                            {!allday ? 'When:' : 'Date:'}
                        </label>
                        {!allday ? (
                            <DateTimeRangePicker
                                onChange={newRange => dispatch(setDaterange([newRange[0].toISOString(), newRange[1].toISOString()]))}
                                value={daterange ? [new Date(daterange[0]), new Date(daterange[1])] : null}
                                className={animate ? 'pulse-once' : ''}
                                calendarClassName="date-picker-calendar-elevated"
                            />
                        ) : (
                            <DatePicker
                                onChange={newDate => dispatch(setDate(newDate.toISOString()))}
                                value={date ? new Date(date) : null}
                                className={animate ? 'pulse-once' : ''}
                                calendarClassName="date-picker-calendar-elevated"
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Time Zone - compact horizontal radio buttons */}
            {!allday && (
                <div className="field mb-3 col-12">
                    <label htmlFor="timezone" className="font-medium text-900">Time Zone</label>
                    <div className="flex flex-wrap gap-3 mt-1">
                        {timeZones.map((zone, index) => (
                            <div key={zone.value} className="flex align-items-center">
                                <RadioButton
                                    inputId={`zone${index + 1}`}
                                    name={zone.label}
                                    value={zone.value}
                                    onChange={(e) => dispatch(setTimeZone(e.value))}
                                    checked={timeZone === zone.value}
                                />
                                <label htmlFor={`zone${index + 1}`} className="ml-2 text-sm">{zone.label}</label>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    )
}

export default DateTimeInfo